import React, { useEffect, useMemo, useState } from 'react';
import { Card, Tooltip, Table, message, Popconfirm } from "antd";
import { MDBCol, MDBIcon, MDBRow } from "mdbreact";
import { Text } from "react-font";
import { Button, SearchInput, TrashIcon } from "evergreen-ui";
import Firebase from "../../Firebase/Firebase";
import FireFetch from "../../Firebase/FireFetch";
import { TableExport } from "tableexport";
import { useListVals } from "../../Firebase/databaseHooks";
import { writeFile, utils } from "xlsx";
import { getDatabase, ref } from "firebase/database";
import { getAuth } from "firebase/auth";
import moment from "moment";
import { useAuth } from "../../AuthContext/AuthContext";
import { useMergedListVals } from "../../Firebase/dataCompat";
import {
	buildLessonReviewAverages,
	formatReviewTimestamp,
	getReviewEndpoint,
	isReviewRecord,
	normalizeReviewRecord,
} from "./reviewCompat";

const db = getDatabase(Firebase);
const auth = getAuth(Firebase);

const Reviews = () => {
	const { canAccessModule } = useAuth();
	const canDeleteReview = canAccessModule("REVIEWS", "delete");
	const [dataArray, setDataArray] = useState([]);
	const [eventsData, setEventsData] = useState([]);
	const [searchValue, setSearchValue] = useState("");

	const [mergedReviews, reviewsLoading] = useMergedListVals(
		"talmidsdb/reviews",
		"talmidsdb/Reviews",
		"talmidsdb/lessonFeedBackForms"
	);
	const [userArray, usersLoading] = useListVals(ref(db, "talmidsdb/users"));
	const [communityArray, communitiesLoading] = useListVals(ref(db, "talmidsdb/communities"));
	const [lessonArray, lessonsLoading] = useListVals(ref(db, "talmidsdb/lessons"));
	const reviewArray = useMemo(
		() => mergedReviews.map(normalizeReviewRecord).filter(isReviewRecord),
		[mergedReviews]
	);
	const loading = reviewsLoading || usersLoading || communitiesLoading || lessonsLoading;

	useEffect(() => {
		if (reviewArray && userArray && communityArray && lessonArray) {
			const tempArray = reviewArray.map((item) => ({
				key: item.reviewID || item.id,
				rating: (
					<>
						{item?.rating} <MDBIcon icon="star" className="amber-text" />
					</>
				),
				community:
					communityArray.find((x) => x.id === item?.communityID || x.communityID === item?.communityID)?.name || "Unknown community",
				lesson:
					lessonArray.find((x) => x.id === item.lessonID || x.lessonID === item.lessonID)?.lessonName || "Unknown lesson",
				assigned: (() => {
					const user = userArray.find((x) => x.id === (item.userID || item.targetUserID || item.createdBy));
					return user?.firstname && user?.surname
						? `${user.firstname} ${user.surname}`
						: "Unknown user";
				})(),
				comment: item.comment || "-",
				time: formatReviewTimestamp(item.timeStamp),
				action: (
					canDeleteReview ? (
						<Popconfirm
							title="Are you sure you want to delete this review?"
							onConfirm={() => {
								if (!canDeleteReview) {
									message.error("You do not have permission to delete reviews.");
									return;
								}

								FireFetch.DeleteFromDB(getReviewEndpoint(item), item.reviewID || item.id)
									.then((text) => {
										if (text === "success") {
											FireFetch.addLog("reviews", item.reviewID || item.id, "Delete");
											message.success("Review deleted");
										} else {
											message.error("Unable to delete");
										}
									})
									.catch((error) => {
										message.error("Unable to delete: " + error.message);
									});
							}}
							okText="Yes"
							cancelText="No"
						>
							<Button intent="danger">
								<TrashIcon color="danger" />
							</Button>
						</Popconfirm>
					) : null
				),
			}));

			setDataArray(tempArray);
			setEventsData(tempArray);
		}
	}, [communityArray, lessonArray, reviewArray, userArray, canDeleteReview]);

	const handleSearch = (searchText) => {
		const needle = String(searchText || "").toLowerCase();
		const filtered = dataArray.filter(({ lesson, community, assigned, comment, time }) =>
			[lesson, community, assigned, comment, time]
				.some((value) => String(value || "").toLowerCase().includes(needle))
		);
		setEventsData(filtered);
	};

	const exportXL = (title) => {
		const tableElement = document.getElementById("dataTable");
		if (!tableElement) return;

		const table = TableExport(tableElement, {
			filename: title,
			exportButtons: false,
			sheetname: title,
		});

		const exportData = table.getExportData();
		const csv = exportData.dataTable.csv;
		table.export2file(
			csv.data,
			csv.mimeType,
			csv.filename,
			csv.fileExtension,
			csv.merges,
			csv.RTL,
			csv.sheetname
		);
	};

	const downloadExcel = (data) => {
		const worksheet = utils.json_to_sheet(data);
		const workbook = utils.book_new();
		utils.book_append_sheet(workbook, worksheet, "reviews");
		writeFile(workbook, "AverageRating.xlsx");
	};

	const calculateAverageRating = () => {
		downloadExcel(buildLessonReviewAverages(lessonArray, reviewArray));
	};

	const columns = [
		{
			title: 'Rating',
			dataIndex: 'rating',
			key: 'rating',
		},
		{
			title: 'Lesson',
			dataIndex: 'lesson',
			key: 'lesson',
			sorter: (a, b) => a.lesson.localeCompare(b.lesson),
		},
		{
			title: 'Community',
			dataIndex: 'community',
			key: 'community',
			sorter: (a, b) => a.community.localeCompare(b.community),
		},
		{
			title: 'User',
			dataIndex: 'assigned',
			key: 'assigned',
			sorter: (a, b) => a.assigned.localeCompare(b.assigned),
			responsive: ['sm'],
		},
		{
			title: 'Comment',
			dataIndex: 'comment',
			key: 'comment',
			sorter: (a, b) => a.comment.localeCompare(b.comment),
			responsive: ['sm'],
			render: (text) => (
				<span className="interaction-export-wrapper">
					<Tooltip title={<span style={{ color: 'black' }}>{text}</span>} overlayInnerStyle={{ backgroundColor: 'white' }}>
						<a>{text?.length > 20 ? `${text.substring(0, 20)}...` : text}</a>
					</Tooltip>
					<span className="full-text-for-export" style={{ display: 'none' }}>{text}</span>
				</span>
			),
		},
		{
			title: 'Time',
			dataIndex: 'time',
			key: 'time',
			sorter: (a, b) =>
				moment(a.time, "DD/MMM/YYYY HH:mm").diff(moment(b.time, "DD/MMM/YYYY HH:mm")),
			responsive: ['sm'],
		},
		{
			title: 'Action',
			dataIndex: 'action',
			key: 'action',
		},
	];

	return (
		<div className="font-Nunito">
			<div className="d-flex justify-content-center align-items-center pt-4 mt-4">
				<MDBRow className="w-100">
					<MDBCol md={12}>
						<Card className="w-100">
							<MDBRow>
								<MDBCol md={12}>
									<div className="text-left">
										<div className="d-flex flex-row justify-content-between">
											<div>
												<h5 className="font-weight-bold" style={{ color: '#51195D' }}>
													<Text family="Nunito">Reviews</Text>
												</h5>
											</div>
											<div className="d-flex flex-row">
												<SearchInput
													className="mx-1"
													placeholder="Filter through lessons or communities"
													onChange={(e) => {
														setSearchValue(e.target.value);
														handleSearch(e.target.value);
													}}
													value={searchValue}
												/>
												<Button
													intent="primary"
													appearance="primary"
													onClick={() => exportXL("Reviews")}
													className="mx-1"
												>
													Print
												</Button>
												<Button
													intent="primary"
													appearance="primary"
													onClick={calculateAverageRating}
													className="mx-1"
												>
													Get Average
												</Button>
											</div>
										</div>
									</div>
								</MDBCol>
							</MDBRow>
						</Card>
						<Card>
							<Table
								id="dataTable"
								className="custom-table"
								style={{ overflow: "auto" }}
								pagination={{
									defaultPageSize: 100,
									showSizeChanger: true,
									pageSizeOptions: ["100", "200", "300"],
								}}
								columns={columns}
								loading={loading}
								dataSource={eventsData}
							/>
						</Card>
					</MDBCol>
				</MDBRow>
			</div>
		</div>
	);
};

export default Reviews;
